/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import {
  KnativeService,
  KNATIVE_SERVICE_ANNOTATION,
  KNATIVE_NAMESPACE_ANNOTATION,
} from '../types';

export interface UseKnativeServiceResult {
  service: KnativeService | undefined;
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

/**
 * Hook to fetch Knative Service for the current entity.
 *
 * @param clusterName - Optional cluster name to query. If not provided, queries all clusters.
 * @returns Knative Service data, loading state, and error
 *
 * @public
 */
export function useKnativeService(
  clusterName?: string,
  refreshIntervalMs: number = 0,
): UseKnativeServiceResult {
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);

  const [service, setService] = useState<KnativeService | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasLoadedRef = useRef(false);

  const serviceName = entity.metadata.annotations?.[KNATIVE_SERVICE_ANNOTATION];
  const serviceNamespace =
    entity.metadata.annotations?.[KNATIVE_NAMESPACE_ANNOTATION] ||
    entity.metadata.namespace ||
    'default';

  // 从 Component 注解读取集群名称
  const getClusterFromAnnotations = (): string | undefined => {
    const annotations = entity.metadata.annotations || {};
    // 优先使用显式传入的 clusterName
    if (clusterName) return clusterName;
    // 从 template 生成的注解中读取集群名称
    return annotations['backstage.io/kubernetes-cluster-name'];
  };

  const fetchService = useCallback(async () => {
    if (!serviceName) {
      setService(undefined);
      setLoading(false);
      setError(new Error('No Knative service annotation found on entity'));
      return;
    }

    // 仅首次加载时显示 loading，轮询刷新时静默更新
    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(undefined);

    try {
      // 优先从注解读取，其次使用传入的 clusterName，最后尝试默认集群
      const targetCluster = getClusterFromAnnotations() || 'local-cluster'; // 默认使用 local-cluster（测试集群）

      const response = await kubernetesApi.proxy({
        clusterName: targetCluster,
        path: `/apis/serving.knative.dev/v1/namespaces/${serviceNamespace}/services/${serviceName}`,
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(
            `Knative Service "${serviceName}" not found in namespace "${serviceNamespace}"`,
          );
        }
        throw new Error(`Failed to fetch Knative Service: ${response.statusText}`);
      }

      const data = (await response.json()) as KnativeService;
      setService(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setService(undefined);
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [kubernetesApi, serviceName, serviceNamespace, clusterName, entity, refreshKey]);

  useEffect(() => {
    fetchService();
  }, [fetchService]);

  // 自动轮询（静默刷新，不重置 loading 状态）
  useEffect(() => {
    if (!refreshIntervalMs || refreshIntervalMs <= 0) return undefined;
    const timer = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, refreshIntervalMs);
    return () => clearInterval(timer);
  }, [refreshIntervalMs]);

  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return { service, loading, error, refresh };
}

